import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, Repeat, Shield, Star, Truck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";



import { useAnalytics } from "@/hooks/useAnalytics";

export const HeroSection = () => {
  const { t } = useTranslation();
  const { getVariant } = useAnalytics();
  const isMobile = useIsMobile();
  
  // A/B Test for Hero Headline
  const heroVariant = getVariant('hero-headline-test');
  const headline = heroVariant === 'variant-b' 
    ? "Premium Quality Devices" 
    : t("hero.heroTitle");

  const prefersReducedMotion = useReducedMotion();
  const disableWordAnimation = !!prefersReducedMotion;
  const [wordIndex, setWordIndex] = useState(0);

  const rotatingWords = [
    t("hero.rotatingWord0"),
    t("hero.rotatingWord1"),
    t("hero.rotatingWord2"),
    t("hero.rotatingWord3"),
  ];

  useEffect(() => {
    if (disableWordAnimation) return;

    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [disableWordAnimation]);

  return (
    <section className="relative w-full max-w-full overflow-x-clip overflow-y-hidden min-h-[80vh] sm:min-h-[85vh] flex items-center justify-center bg-background">

      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 w-full max-w-full relative z-10 py-16 sm:py-24">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <h1 className="max-w-full text-[2.25rem] leading-[1.1] sm:text-5xl lg:text-6xl xl:text-7xl font-display font-extrabold mb-6 sm:mb-8 text-foreground tracking-tight">
            {headline}
            <br />
            <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              {t("hero.heroTitleHighlight")}{" "}
              <span className="inline-block relative min-w-[88px] sm:min-w-[200px] lg:min-w-[260px] max-w-full text-left align-bottom" aria-live="polite" aria-atomic="true">
                {disableWordAnimation ? (
                  <span className="inline-block bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                    {rotatingWords[0]}
                  </span>
                ) : (
                  <AnimatePresence mode="wait">
                    <m.span
                      key={rotatingWords[wordIndex]}
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -30, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="inline-block bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent"
                    >
                      {rotatingWords[wordIndex]}
                    </m.span>
                  </AnimatePresence>
                )}
              </span>
            </span>
          </h1>

          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-10 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-14">
            <Button size="lg" className="shadow-lg shadow-primary/20 group text-sm sm:text-base px-8 sm:px-10 h-12 sm:h-14 rounded-full font-semibold" asChild>
              <Link to="/shop">
                {t("hero.shopNow")} <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ms-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="text-sm sm:text-base px-8 sm:px-10 h-12 sm:h-14 rounded-full font-semibold border-2" asChild>
              <Link to="/sell">
                <Repeat className="w-4 h-4 sm:w-5 sm:h-5 me-2" /> {t("hero.sellPhone")}
              </Link>
            </Button>
          </div>

          <m.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 text-muted-foreground text-xs sm:text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <span className="font-medium">{t("hero.certified")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Truck className="w-4 h-4 text-primary" />
              </div>
              <span className="font-medium">{t("hero.freeDelivery")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Star className="w-4 h-4 text-primary" />
              </div>
              <span className="font-medium">{t("hero.rating")}</span>
            </div>
          </m.div>
        </m.div>
      </div>

    </section>
  );
};
